import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Download, Copy, AlertTriangle, ArrowLeft, Clapperboard, Check,
  Brain, Mic, ImageIcon, Clock, RefreshCw, Users, ExternalLink, Link2, Tag, Eye, MousePointerClick,
  Share2, Youtube, Facebook, Instagram, Music2, CircleDot, XCircle, CheckCircle2, Send, Bookmark, Shuffle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getVideo, retryVideo, trackView, goUrl, mediaUrl, API, getVideoPosts, retryPost, cancelPost, savePresetFromVideo, errText } from "../lib/api";
import VerticalPlayer from "../components/VerticalPlayer";
import PublishDialog from "../components/PublishDialog";
import RemixDialog from "../components/RemixDialog";
import { devError } from "../lib/log";

const PROGRESS_TRANSITION = { duration: 0.6 };
const PLAT_ICON = { youtube: Youtube, tiktok: Music2, instagram: Instagram, facebook: Facebook };
const STATUS_UI = {
  pending: { label: "Pending", cls: "text-slate-400", Icon: CircleDot },
  scheduled: { label: "Scheduled", cls: "text-sky-400", Icon: Clock },
  uploading: { label: "Uploading", cls: "text-amber-400", Icon: Loader2 },
  published: { label: "Published", cls: "text-emerald-400", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "text-red-400", Icon: XCircle },
  reauth_required: { label: "Reconnect needed", cls: "text-orange-400", Icon: AlertTriangle },
  cancelled: { label: "Cancelled", cls: "text-slate-500", Icon: XCircle },
};

const STAGES = [
  { key: "brief", label: "Analyzing brief", at: 5 },
  { key: "script", label: "Writing 5-beat script", at: 12 },
  { key: "scenes", label: "Generating visuals", at: 18 },
  { key: "voice", label: "Synthesizing voiceover", at: 45 },
  { key: "render", label: "Stitching video", at: 84 },
];

function Processing({ project, stalled }) {
  const p = project.progress || 0;
  const queued = (project.stage || "").toLowerCase().startsWith("queued");
  const pos = project.queue_position;
  return (
    <div className="max-w-xl mx-auto text-center py-16">
      <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
        <div className="absolute inset-0 rounded-full border-2 border-white/10" />
        <div className="absolute inset-0 rounded-full border-2 border-[#FF453A] border-t-transparent animate-spin" />
        <Clapperboard className="h-9 w-9 text-[#FF6A5E]" />
      </div>
      <h2 className="mt-8 font-display font-extrabold text-2xl">{queued ? "In the render queue…" : "Directing your ad…"}</h2>
      <p className="mt-2 text-[#FF6A5E] font-medium">{project.stage}</p>

      {queued && pos ? (
        <div data-testid="queue-position" className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-[#FF453A]/25 bg-[#FF453A]/10 px-6 py-4">
          <Users className="h-5 w-5 text-[#FF6A5E]" />
          <div className="text-left">
            <p className="font-display font-extrabold text-xl leading-none">You're #{pos} in line</p>
            <p className="mt-1 text-xs text-slate-400">We render one ad at a time for top quality — yours starts automatically.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-8 h-2 w-full rounded-full bg-[#1E2333] overflow-hidden">
            <motion.div className="h-full bg-[#FF453A]" animate={{ width: `${p}%` }} transition={PROGRESS_TRANSITION} />
          </div>
          <p className="mt-2 text-sm text-slate-500 font-code">{p}%</p>
        </>
      )}

      {stalled && (
        <div data-testid="stall-warning" className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-left">
          <p className="text-sm text-amber-300 font-medium">This is taking longer than usual.</p>
          <p className="mt-1 text-sm text-slate-400">If it doesn't move in a moment, it may have stalled — you can start a fresh one.</p>
          <Link to="/studio" data-testid="stall-restart-btn" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6A5E] hover:text-white transition-colors">
            <RefreshCw className="h-4 w-4" /> Start a new ad
          </Link>
        </div>
      )}

      {!queued && (
        <div className="mt-10 space-y-3 text-left">
          {STAGES.map((s) => {
            const done = p > s.at + 5;
            const active = !done && p >= s.at - 1;
            return (
              <div key={s.key} className="flex items-center gap-3">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full ${done ? "bg-[#FF453A]" : active ? "bg-[#FF453A]/20" : "bg-[#1E2333]"}`}>
                  {done ? <Check className="h-4 w-4 text-white" /> : active ? <Loader2 className="h-3.5 w-3.5 text-[#FF6A5E] animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />}
                </span>
                <span className={`text-sm ${done || active ? "text-white" : "text-slate-500"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function VideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [stalled, setStalled] = useState(false);
  const pollRef = useRef(null);
  const fetchItRef = useRef(null);
  const lastChangeRef = useRef({ progress: -1, at: Date.now() });
  const [retrying, setRetrying] = useState(false);
  const viewedRef = useRef(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const postsPollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchIt = async () => {
      try {
        const p = await getVideo(id);
        if (!mounted) return;
        setProject(p);
        // stall detection: warn if progress hasn't advanced for 90s while actively rendering
        const queued = (p.stage || "").toLowerCase().startsWith("queued");
        if (p.status === "processing" && !queued) {
          if (p.progress !== lastChangeRef.current.progress) {
            lastChangeRef.current = { progress: p.progress, at: Date.now() };
            setStalled(false);
          } else if (Date.now() - lastChangeRef.current.at > 90000) {
            setStalled(true);
          }
        } else {
          lastChangeRef.current = { progress: p.progress, at: Date.now() };
          setStalled(false);
        }
        if (p.status !== "processing" && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        toast.error("Video not found");
        navigate("/library");
      }
    };
    fetchItRef.current = fetchIt;
    fetchIt();
    pollRef.current = setInterval(fetchIt, 3000);
    return () => { mounted = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, [id, navigate]);

  const retry = async () => {
    setRetrying(true);
    try {
      const p = await retryVideo(id);
      setProject(p);
      lastChangeRef.current = { progress: p.progress, at: Date.now() };
      setStalled(false);
      if (!pollRef.current && fetchItRef.current) {
        pollRef.current = setInterval(fetchItRef.current, 3000);
      }
      toast.success("Re-generating your ad");
    } catch {
      toast.error("Could not retry");
    }
    setRetrying(false);
  };

  useEffect(() => {
    if (project?.status === "completed" && !viewedRef.current) {
      viewedRef.current = true;
      trackView(id);
    }
  }, [project, id]);

  const loadPosts = async () => {
    try {
      const list = await getVideoPosts(id);
      setPosts(list);
      const active = list.some((p) => ["pending", "scheduled", "uploading"].includes(p.status));
      if (active && !postsPollRef.current) {
        postsPollRef.current = setInterval(loadPosts, 3000);
      } else if (!active && postsPollRef.current) {
        clearInterval(postsPollRef.current);
        postsPollRef.current = null;
      }
    } catch (e) { devError("Failed to load posts:", e); }
  };

  useEffect(() => {
    if (project?.status === "completed") loadPosts();
    return () => { if (postsPollRef.current) { clearInterval(postsPollRef.current); postsPollRef.current = null; } };
  }, [project?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRetryPost = async (jobId) => {
    try { await retryPost(jobId); toast.success("Retrying"); loadPosts(); }
    catch { toast.error("Could not retry"); }
  };
  const onCancelPost = async (jobId) => {
    try { await cancelPost(jobId); toast.success("Cancelled"); loadPosts(); }
    catch { toast.error("Could not cancel"); }
  };


  const aff = project?.affiliate && project.affiliate.enabled ? project.affiliate : null;
  const openAffiliate = (src) => {
    if (!aff) return;
    const url = aff.tracking_enabled ? goUrl(id, src) : aff.url;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const copy = (text, label) => { navigator.clipboard.writeText(text || ""); toast.success(`${label} copied`); };
  const suggestedCaption = () => {
    const parts = [];
    if (aff?.product_name) parts.push(`${aff.product_name} —`);
    parts.push(project?.title || "Check this out");
    if (aff?.cta_text) parts.push(`\n\n${aff.cta_text}: ${aff.url}`);
    if (aff?.discount_code) parts.push(`\nUse code: ${aff.discount_code}`);
    if (aff?.disclosure_enabled) parts.push(`\n\n${aff.disclosure_text}`);
    parts.push(`\n\n#ad #${(aff?.product_name || project?.title || "product").replace(/[^a-z0-9]/gi, "")}`);
    return parts.join(" ").replace(" \n", "\n");
  };

  const copyScript = () => {
    const text = (project.scenes || []).map((s) => `[${s.type}] ${s.narration}`).join("\n\n");
    navigator.clipboard.writeText(text);
    toast.success("Script copied to clipboard");
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(mediaUrl(project.video_path));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.title.replace(/[^a-z0-9]/gi, "_")}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    setDownloading(false);
  };

  const [remixOpen, setRemixOpen] = useState(false);

  const [savingPreset, setSavingPreset] = useState(false);
  const saveAsPreset = async () => {
    setSavingPreset(true);
    try {
      const p = await savePresetFromVideo(project.id);
      const kept = p.affiliate?.url ? " — affiliate link kept" : "";
      toast.success(`Saved "${p.name}" as a preset${kept}`, {
        action: { label: "New video from it", onClick: () => navigate(`/studio?preset=${p.id}`) },
      });
    } catch (e) { toast.error(errText(e)); }
    setSavingPreset(false);
  };

  if (!project) {
    return <div className="flex items-center justify-center py-40 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/library" data-testid="back-to-library" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Library
      </Link>

      {project.status === "processing" && <Processing project={project} stalled={stalled} />}

      {project.status === "failed" && (
        <div className="max-w-lg mx-auto text-center py-20">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/12 border border-red-500/25">
            <AlertTriangle className="h-8 w-8 text-red-400" />
          </span>
          <h2 className="mt-5 font-display font-bold text-2xl">Generation failed</h2>
          <p className="mt-2 text-slate-400 text-sm">{project.error || "Something went wrong."}</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button data-testid="retry-btn" onClick={retry} disabled={retrying} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-6 transition-colors disabled:opacity-60">
              {retrying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Retry same brief
            </Button>
            <Button data-testid="new-ad-btn" onClick={() => navigate("/studio")} variant="outline" className="rounded-full px-6 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white transition-colors">
              Start fresh
            </Button>
          </div>
        </div>
      )}

      {project.status === "completed" && (
        <div className="grid lg:grid-cols-[auto_1fr] gap-12">
          <div className="flex justify-center">
            <VerticalPlayer
              scenes={project.scenes}
              affiliate={project.affiliate}
              onLink={openAffiliate}
              captionsEnabled={project.captions_enabled}
              captionStyle={project.caption_style || null}
              aspect={project.aspect_ratio || "9:16"}
            />
          </div>

          <div>
            <span className="eyebrow">Finished ad</span>
            <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{project.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-400">
              <span className="flex items-center gap-1.5" data-testid="final-duration"><Clock className="h-4 w-4" /> {(project.final_duration ?? project.duration)}s final length</span>
              <span className="flex items-center gap-1.5 capitalize"><Brain className="h-4 w-4" /> {project.model}</span>
              <span className="flex items-center gap-1.5 capitalize"><Mic className="h-4 w-4" /> {project.voice}</span>
              <span className="flex items-center gap-1.5 capitalize"><ImageIcon className="h-4 w-4" /> {project.visual_source}</span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="download-btn" onClick={download} disabled={downloading} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-6 font-semibold transition-colors glow-coral">
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download MP4
              </Button>
              <Button data-testid="copy-script-btn" onClick={copyScript} variant="outline" className="rounded-full px-6 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white transition-colors">
                <Copy className="h-4 w-4 mr-2" /> Copy script
              </Button>
              <Button data-testid="open-publish-btn" onClick={() => setPublishOpen(true)} variant="outline" className="rounded-full px-6 border-[#FF453A]/40 bg-[#FF453A]/10 text-[#FF6A5E] hover:bg-[#FF453A]/20 hover:text-white transition-colors">
                <Share2 className="h-4 w-4 mr-2" /> Publish to social
              </Button>
              <Button data-testid="remix-btn" onClick={() => setRemixOpen(true)} className="rounded-full px-6 bg-[#FF453A] hover:bg-[#ff5c52] text-white transition-colors">
                <Shuffle className="h-4 w-4 mr-2" /> Remix
              </Button>
              <RemixDialog video={project} open={remixOpen} onOpenChange={setRemixOpen} />
              <Button data-testid="save-preset-btn" onClick={saveAsPreset} disabled={savingPreset} variant="outline" className="rounded-full px-6 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white transition-colors">
                {savingPreset ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bookmark className="h-4 w-4 mr-2" />} Save as preset
              </Button>
            </div>

            {aff && (
              <div data-testid="affiliate-panel" className="mt-8 rounded-2xl border border-[#FF453A]/25 bg-[#FF453A]/[0.06] p-5">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-[#FF6A5E]" />
                  <h3 className="font-display font-bold text-base">Affiliate promotion</h3>
                </div>
                {aff.product_name && <p className="mt-2 text-sm text-slate-300">Promoting <strong className="text-white">{aff.product_name}</strong></p>}
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="flex items-center gap-1.5 text-slate-400"><Eye className="h-4 w-4" /> {project.view_count || 0} views</span>
                  <span className="flex items-center gap-1.5 text-slate-400"><MousePointerClick className="h-4 w-4" /> {(project.affiliate_click_count || 0) + (project.cta_click_count || 0)} clicks</span>
                </div>
                <Button data-testid="open-affiliate-btn" onClick={() => openAffiliate("cta")} className="mt-4 rounded-full px-5 text-white font-semibold" style={{ backgroundColor: aff.cta?.bg_color || "#FF453A" }}>
                  {aff.cta?.text || aff.cta_text || "Shop Now"} <ExternalLink className="h-4 w-4 ml-2" />
                </Button>

                {aff.disclosure_enabled && (
                  <p data-testid="page-disclosure" className="mt-4 text-xs text-slate-400 border-t border-white/10 pt-3">{aff.disclosure_text}</p>
                )}

                <div className="mt-5">
                  <p className="eyebrow mb-2">Share &amp; export</p>
                  <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 mb-3">
                    <p className="text-xs text-amber-200/90 leading-relaxed">
                      A downloaded MP4 is <strong>not</strong> a universally clickable link. On TikTok / Reels / Shorts / Facebook, add your link using that platform's bio link, shopping tag or "link" sticker. On the Faceless Ad Studio page above, the video &amp; button are fully clickable.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="copy-link-btn" onClick={() => copy(aff.url, "Affiliate link")} variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5"><Link2 className="h-3.5 w-3.5 mr-1.5" /> Copy link</Button>
                    <Button data-testid="copy-caption-btn" onClick={() => copy(suggestedCaption(), "Caption")} variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy caption</Button>
                    <Button data-testid="copy-cta-btn" onClick={() => copy(aff.cta?.text || aff.cta_text, "CTA")} variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy CTA</Button>
                    {aff.disclosure_enabled && <Button data-testid="copy-disclosure-btn" onClick={() => copy(aff.disclosure_text, "Disclosure")} variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy disclosure</Button>}
                  </div>
                </div>
              </div>
            )}

            {posts.length > 0 && (
              <div data-testid="publish-status-section" className="mt-8">
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-[#FF6A5E]" />
                  <h3 className="font-display font-bold text-lg">Publishing status</h3>
                </div>
                <div className="mt-4 space-y-2">
                  {posts.map((job) => {
                    const PIcon = PLAT_ICON[job.platform] || Send;
                    const S = STATUS_UI[job.status] || STATUS_UI.pending;
                    return (
                      <div key={job.id} data-testid={`post-${job.id}`} className="rounded-xl border border-white/10 bg-[#11141E] px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 min-w-0">
                            <PIcon className="h-4 w-4 shrink-0" />
                            <span className="text-sm text-slate-200 truncate">{job.platform_name}</span>
                          </span>
                          <span className="flex items-center gap-3 shrink-0">
                            <span className={`flex items-center gap-1.5 text-xs font-medium ${S.cls}`} data-testid={`status-${job.id}`}>
                              <S.Icon className={`h-3.5 w-3.5 ${job.status === "uploading" ? "animate-spin" : ""}`} />
                              {S.label}
                              {job.status === "scheduled" && job.scheduled_at && (
                                <span className="text-slate-500 font-normal">· {new Date(job.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                              )}
                            </span>
                            {["failed", "reauth_required"].includes(job.status) && (
                              <Button data-testid={`retry-post-${job.id}`} size="sm" variant="outline" onClick={() => onRetryPost(job.id)} className="h-7 rounded-full border-white/15 bg-transparent text-xs text-slate-200 hover:bg-white/5">
                                <RefreshCw className="h-3 w-3 mr-1" /> Retry
                              </Button>
                            )}
                            {["pending", "scheduled"].includes(job.status) && (
                              <Button data-testid={`cancel-post-${job.id}`} size="sm" variant="outline" onClick={() => onCancelPost(job.id)} className="h-7 rounded-full border-white/15 bg-transparent text-xs text-slate-400 hover:bg-white/5">
                                Cancel
                              </Button>
                            )}
                          </span>
                        </div>
                        {job.error && <p className="mt-1.5 text-xs text-red-400/80">{job.error}</p>}
                        {job.status === "reauth_required" && (
                          <Link to="/connections" className="mt-1 inline-block text-xs text-[#FF6A5E] underline">Reconnect account →</Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h3 className="mt-10 font-display font-bold text-lg">Scene breakdown</h3>
            <div className="mt-4 space-y-3">
              {project.scenes.map((s, i) => (
                <div key={s.image_path || s.audio_path || i} data-testid={`scene-row-${i}`} className="rounded-2xl border border-white/10 bg-[#11141E] p-4 flex gap-4">
                  <div className="relative h-24 w-[54px] shrink-0 rounded-lg overflow-hidden border border-white/10">
                    <img src={mediaUrl(s.image_path)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="eyebrow">{s.type}</span>
                      <span className="text-[11px] text-slate-500 font-code">{s.duration}s</span>
                    </div>
                    <p className="mt-1 font-display font-bold text-sm text-white uppercase">{s.caption}</p>
                    <p className="mt-1 text-sm text-slate-400 leading-snug">{s.narration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <PublishDialog
        videoId={id}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onPublished={() => loadPosts()}
      />
    </div>
  );
}
